'use client';

import { useParams, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

/**
 * Opens the document a `?docId` search result points at, once the meeting's documents have loaded.
 *
 * The applied id is remembered per meeting, so closing the document does not immediately reopen it
 * while the parameter is still in the URL, yet the same `?docId` on a different meeting still opens.
 *
 * @param documents - The tab's documents, searched for the linked id.
 * @param onOpen - Opens the matched document. Read through a ref, so an inline callback is fine.
 */
export function useDocumentDeepLink<T extends { id: number }>(
  documents: T[],
  onOpen: (document: T) => void
) {
  const params = useParams<{ id: string }>();
  const searchParams = useSearchParams();
  const docIdParam = searchParams.get('docId');
  const appliedDocRef = useRef<string | null>(null);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    const token = `${params.id}:${docIdParam}`;
    if (!docIdParam || appliedDocRef.current === token) {
      return;
    }

    const match = documents.find((document) => String(document.id) === docIdParam);
    if (!match) {
      return;
    }

    appliedDocRef.current = token;
    onOpenRef.current(match);
  }, [docIdParam, documents, params.id]);
}
